import React, {useState, useEffect} from 'react';
import b_ from 'b_';

import KebabMenu from 'client/common/components/kebab-menu';

import {INDUSTRY_OPTIONS} from 'client/creatives/components/creative-settings/utils';

import history from 'client/common/utils/history';
import {openLink} from 'client/common/utils/window';
import {getVideoLink} from 'client/common/utils/video';
import {usePreviewUrl} from 'client/common/hooks/video';
import {PaymentStatus} from 'client/common/types';
import {usePaymentStatus} from 'client/common/hooks/payment';

import {SurveyState} from 'client/common/store/reducers/survey/types';
import {Creative, CreativeCreateData, CREATIVE_INFO_FIELDS} from 'client/creatives/types';

import {CreativeInfo} from 'client/creatives/utils/parse';
import I18N from 'assets/i18n/brandlift-project';

import ReplaceButton from './replace-button';

import PlaceholderImage from './placeholder.svg';

import './creative-card.css';

const i18n = I18N.creatives.creativeCard;
const b = b_.with('creative-card');

export type CreativeCardProps = Partial<Creative> &
    Required<Pick<Creative, 'slug' | 'state' | 'name'>> & {
        creative?: CreativeInfo;
        link?: string;
        onCopy?: () => void;
        onReplace?: (creative: CreativeCreateData) => Promise<void>;
        onCancelModeration?: () => void;
        withMenu?: boolean;
    };

function getMenuItems({withMenu, creative, onCopy, onCancelModeration}: CreativeCardProps) {
    if (!withMenu || !creative) {
        return [];
    }

    const items = [];
    const videoLink = getVideoLink(creative.service, creative.id);

    if (videoLink) {
        items.push({
            value: 'watch',
            content: (
                <a
                    className={b('menu-link')}
                    href={videoLink}
                    target='_blank'
                    rel='noopener noreferrer'
                >
                    {i18n.menu.watch}
                </a>
            )
        });
    }

    if (onCopy) {
        items.push({
            value: 'copy',
            content: i18n.menu.copy
        });
    }

    if (onCancelModeration) {
        items.push({
            value: 'cancelModeration',
            content: i18n.menu.cancelModeration
        });
    }

    return items;
}

interface BadgeTextProps {
    state: SurveyState;
    value: number | undefined;
    paymentStatus: PaymentStatus | undefined;
}

const getBadgeText = ({state, value, paymentStatus}: BadgeTextProps) => {
    if (state === SurveyState.Completed && value) {
        return value;
    }
    if (state === SurveyState.Moderation && paymentStatus === PaymentStatus.NOT_PAYED) {
        return i18n.paymentRequired;
    }

    return i18n.state[state];
};

export default function CreativeCard(props: CreativeCardProps) {
    const {
        name,
        state,
        link,
        stat = {},
        creative: {id, previewUrl, service} = {},
        onCopy,
        onReplace,
        onCancelModeration,
        slug
    } = props;

    const paymentStatus = usePaymentStatus(slug);
    const [src, setSrc] = useState<string>(previewUrl ?? PlaceholderImage);

    const {data: loadedPreviewUrl, isFetching} = usePreviewUrl({
        service,
        id,
        isEnabled: Boolean(src)
    });

    useEffect(() => {
        if (!previewUrl && loadedPreviewUrl) {
            setSrc(loadedPreviewUrl);
        }
    }, [loadedPreviewUrl, previewUrl, isFetching]);

    const handleMenuChange = (value: string) => {
        if (value === 'copy' && onCopy) {
            onCopy();
        }

        if (value === 'cancelModeration' && onCancelModeration) {
            onCancelModeration();
        }
    };

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();

        if (link) {
            if (state === SurveyState.Completed) {
                openLink(link);
            } else {
                history.push({pathname: link});
            }
        }
    };

    const {index} = stat;
    const {toBench, value} = index || {};

    const menuItems = getMenuItems(props);

    const badgeText = getBadgeText({state, value, paymentStatus: paymentStatus.data});

    return (
        <div className={b()}>
            <div className={b('preview')}>
                <a href={link} className={b('image-wrapper')} onClick={handleClick}>
                    <img className={b('image')} src={src} />
                </a>
                <div className={b('paranja')} />
                <div
                    className={b('badge', {
                        toBench,
                        state: state.toLowerCase()
                    })}
                >
                    {badgeText}
                </div>
                <div className={b('controls')}>
                    {onReplace && <ReplaceButton onReplace={onReplace} />}
                    {Boolean(menuItems.length) && (
                        <KebabMenu
                            className={b('menu')}
                            view='dark'
                            items={menuItems}
                            onChange={handleMenuChange}
                        />
                    )}
                </div>
            </div>
            <a href={link} className={`${b('name')} ${b('link')}`} onClick={handleClick}>
                {name}
            </a>
            <div className={b('tags')}>
                {CREATIVE_INFO_FIELDS.map(field => {
                    if (props[field]) {
                        return (
                            <div key={`tag-${field}`} className={b('tag')}>
                                {field === 'industry'
                                    ? INDUSTRY_OPTIONS.find(item => item.value === props[field])
                                          ?.content
                                    : props[field]}
                            </div>
                        );
                    }
                })}
            </div>
        </div>
    );
}
